import { Avatar } from "@mui/material";
import Button from '@mui/material/Button';
import React, { useEffect, useState, useRef } from "react";
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import api from "../../services/api";
import './RenderAudioFile.css';

const RenderAudioFile = ({ themeMode, srcAudio, colors, idDiv, avatarUser }) => {
    const [speedAudio, setSpeedAudio] = useState(1);
    const [device, setDevice] = useState("unknown");
    const [audio, setAudio] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const audioRef = useRef(null);
    const playerRef = useRef(null);
    const MAX_RETRIES = 3;

    // Detecta o dispositivo e carrega a velocidade salva no primeiro render
    useEffect(() => {
        const detectDevice = () => {
            const userAgent = navigator.userAgent;
            if (userAgent.match(/iPhone|iPad|iPod/i)) {
                return "iOS";
            } else if (userAgent.match(/Android/i)) {
                return "Android";
            } else if (userAgent.match(/Windows/i)) {
                return "Windows";
            } else {
                return "other";
            }
        };

        setDevice(detectDevice());

        // Busca a velocidade salva anteriormente
        let value = localStorage.getItem('speed');
        if (!value) {
            localStorage.setItem('speed', '1');
            value = 1;
        } else {
            value = parseFloat(value);
        }
        setSpeedAudio(value);
    }, []);

    // Carrega o áudio quando a URL muda ou o dispositivo é detectado
    useEffect(() => {
        let mounted = true;
        
        const loadAudio = async () => {
            // Se ainda não temos a URL de origem ou não sabemos o dispositivo, não faça nada
            if (!srcAudio || device === "unknown") {
                return;
            }
            
            // Para iOS, precisamos criar uma URL temporária
            if (device === "iOS") {
                try {
                    setLoading(true);
                    setError(false);
                    
                    const response = await api.post('/create-temp-audio', {
                        params: { 
                            audioUrl: srcAudio,
                            timestamp: new Date().getTime() // Previne problemas de cache
                        }
                    }, {
                        timeout: 30000 // 30 segundos de timeout
                    });

                    if (mounted && response.data && response.data.tempUrl) {
                        // Configurar a URL completa para o áudio temporário
                        const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
                        const formattedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
                        const fullUrl = `${formattedBaseUrl}${response.data.tempUrl}`;
                        
                        console.log("Áudio iOS carregado:", fullUrl);
                        setAudio(fullUrl);
                        setRetryCount(0); // Zera contagem de tentativas em caso de sucesso
                    } else {
                        console.error("URL temporária não encontrada na resposta");
                        throw new Error("URL temporária não encontrada");
                    }
                } catch (error) {
                    console.error("Erro ao criar URL de áudio:", error.message);
                    
                    if (mounted) {
                        if (retryCount < MAX_RETRIES) {
                            // Tentar novamente após um pequeno atraso
                            setRetryCount(prev => prev + 1);
                            setTimeout(() => loadAudio(), 1500);
                        } else {
                            setError(true);
                            
                            // Em último caso, tenta usar a URL original diretamente
                            console.warn("Usando URL original como fallback após falhas");
                            setAudio(srcAudio);
                        }
                    }
                } finally {
                    if (mounted) {
                        setLoading(false);
                    }
                }
            } else {
                // Para outros dispositivos, use a URL diretamente
                if (mounted) {
                    setAudio(srcAudio);
                    setLoading(false);
                }
            }
        };

        loadAudio();

        return () => {
            mounted = false;
        };
    }, [device, srcAudio, retryCount]);

    // Aplica a velocidade ao player ATUAL (sem mexer em outros via DOM).
    // Antes: document.querySelectorAll('#buttonIdAudio') iterava todos os
    // botões com o mesmo id (HTML inválido) e setava innerText. Cada call
    // criava uma NodeList nova e fazia mutação direta no DOM dos outros
    // RenderAudioFile, atrapalhando o React e gerando trabalho desnecessário.
    // Agora cada instância só lida com o próprio áudio; o texto do botão é
    // controlado por estado React (speedAudio) e o localStorage só serve
    // para herdar a preferência ao MONTAR um novo player.
    const applySpeedToCurrentPlayer = (speed) => {
        if (device === "iOS" && audioRef.current) {
            audioRef.current.playbackRate = speed;
        } else if (playerRef.current && playerRef.current.audio && playerRef.current.audio.current) {
            playerRef.current.audio.current.playbackRate = speed;
        }
    };

    const changeSpeedInElement = () => {
        applySpeedToCurrentPlayer(speedAudio);
    };

    // Alternar entre as velocidades de reprodução (1x, 1.5x, 2x)
    const changeSpeedAudio = (speed) => {
        const newSpeed = speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1;
        setSpeedAudio(newSpeed);
        localStorage.setItem('speed', newSpeed.toString());
        applySpeedToCurrentPlayer(newSpeed);
    };

    // Função para atualizar a velocidade quando o áudio é carregado, sem iniciar a reprodução
    const handleLoadedData = () => {
        applySpeedToCurrentPlayer(speedAudio);
    };

    // Gerenciar tentativas de recarregar após erro
    const handleRetry = () => {
        setRetryCount(0);
        setError(false);
        setLoading(true);
    };

    // Renderização para dispositivos iOS
    if (device === "iOS") {
        return (
            <div className="render_audio" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingRight: '5px', marginBottom: '10px' }}>
                {loading ? (
                    <p>Carregando áudio...</p>
                ) : error ? (
                    <div style={{ textAlign: "center", width: '100%' }}>
                        <p>Falha ao carregar o áudio.</p>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleRetry}
                            style={{ marginTop: '5px' }}
                        >
                            Tentar novamente
                        </Button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <audio
                            ref={audioRef}
                            controls
                            controlsList="nodownload"
                            preload="metadata"
                            playsInline
                            style={{ flex: 1, maxWidth: '200px' }}
                            src={audio}
                            onPlay={changeSpeedInElement}
                            onLoadedData={handleLoadedData}
                            autoPlay={false}
                        />
                        <div className="playAudioTag" style={{ marginLeft: '10px' }}>
                            <Button
                                size="small"
                                sx={{
                                    padding: '0px 5px !important',
                                    minWidth: '45px',
                                    borderRadius: '15px'
                                }}
                                onClick={() => changeSpeedAudio(speedAudio)}
                                variant="outlined"
                            >{`${speedAudio}X`}</Button>
                        </div>
                        <div style={{ margin: '2.5px', marginLeft: '10px' }} className="avatarUser">
                            <Avatar src={avatarUser} />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Renderização para outros dispositivos (não-iOS)
    return (
        <>
            <style>
                {`
                .rhap_container{
                    background-color: #fff0 !important; 
                    box-shadow: 0 0 3px 0 rgb(0 0 0 / 0%) !important; 
                }
                .rhap_time {
                    color: ${themeMode === 'dark' ? '#fff' : '#212b36'}
                }
                .rhap_main-controls-button{
                    color: ${themeMode === 'dark' ? '#fff' : '#212b36'}
                }
                .rhap_progress-indicator{
                    width: 5px;
                    margin-left: -5px;
                    background: ${themeMode === 'dark' ? '#fff' : '#212b36'}
                }
                @media screen and (max-width: 1200px){
                    .render_audio {
                        width: 250px !important;
                    }
                    .rhap_container {
                        padding: 5px !important;
                    }
                }
                `}
            </style>
            <div className="render_audio" style={{ width: 300, display: 'flex', alignItems: 'center', paddingRight: '5px' }}>
                <AudioPlayer
                    ref={playerRef}
                    showSkipControls={false}
                    layout="horizontal-reverse"
                    autoPlay={false}
                    autoPlayAfterSrcChange={false}
                    src={audio}
                    onPlay={changeSpeedInElement}
                    customAdditionalControls={[]}
                    onLoadedData={handleLoadedData}
                />
                <div className="playAudioTag">
                    <Button
                        size="small"
                        sx={{
                            padding: '0px 5px !important',
                            minWidth: '45px',
                            borderRadius: '15px'
                        }}
                        onClick={() => changeSpeedAudio(speedAudio)}
                        variant="outlined"
                    >{`${speedAudio}X`}</Button>
                </div>
                <div style={{ margin: '2.5px' }} className="avatarUser">
                    <Avatar src={avatarUser} />
                </div>
            </div>
        </>
    );
};

export default RenderAudioFile;